import { debounce } from 'lodash'
import moment from 'moment-timezone'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Select from '../../../components/textFields/AppSelect'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import { KTSVG } from '../../../_metronic/helpers'
import { PageTitle } from '../../../_metronic/layout/core'
import DateRangeComp from '../../../components/dateComponent/DateRangeComp'
import Pagination from '../../../components/pagination/Pagination'
import SearchBarComponent from '../../../components/searchBar/SearchBarComponent'
import { constraintConfig } from '../../../constraintConfig'
import { selectAuth } from '../../../features/authSlice'
import { useAppSelector } from '../../../hooks'
import { useDepartmentsLookupQuery, useGetAllDesignationWithoutRoleQuery, useGetAllManagersLookupQuery, useGetAllOrganizationLookUpQuery, useGetAllUserTypeLookupQuery, useLazyGetPermissionsListQuery } from '../../../services/GeneralApi'
import { useGetOrganizationAllRolesQuery } from '../../../services/OrganizationApi'
import { useGetAllUsersQuery, useLazyGetAllUsersExportReportQuery, useLazyGetOrgUserLoggedInPresenceQuery, useLazyGetUserAccountDetailQuery } from '../../../services/authApi'
import { subscribeUserPresence, UserPresencePayload } from '../../../services/ssExpensesHub'
import labelKey from "../../localization/label.json"
import tosterKey from "../../localization/toster.json"
import ImpersonateUserModal from './ImpersonateUserModal'
import UserActiveConfirmation from './UserActiveConfirmation'
import UserDetailModal from './UserDetailModal'
import { UpdateUserModal } from './update-user-stepper/UpdateUserModal'
import { toast } from 'react-toastify'
import CurrencyComp from '../../../components/currencyComponent/CurrencyComp'
import DateTimeComp from '../../../components/dateComponent/DateTimeComp'
import { LoadingComponent } from '../../../components/loadingComponent/LoadingComponent'
import NoRecordFound from '../../../components/noRecord/NoRecordFound'
import RefreshComponent from '../../../components/refreshComponent/RefreshComponent'
import TableHeading from '../../../components/tableHeading/TableHeading'
import TableSettingMenu from '../../../components/tableHeading/TableSettingMenu'
import TextField from '../../../components/textFields/TextField'
import useColumnVisibility from '../../customHooks/useColumnVisibility'
import { AddUserModal } from './create-user-stepper/AddUserModal'
import UpdateUserEmailModal from './UpdateUserEmailModal'
import PasswordResetModal from './PasswordResetModal'
import { Alert, Spinner } from 'react-bootstrap'
import { buildPresenceTooltipHtml, LoginSourceWithDate, UserPresenceDot } from './userLoginSource'
import SsRowActions from '../../../components/tableActions/SsRowActions'

interface UserReportProps {
    tabChange?: any;
    excludeSupportUser?: boolean;
}

const canWatchUserPresence = (roleId?: number | null) =>
    roleId === constraintConfig.roleID.role1
    || roleId === constraintConfig.roleID.role2
    || roleId === constraintConfig.roleID.roleIDPortalSupport
    || roleId === constraintConfig.roleID.roleIDAdmin

const UserPage: React.FC<UserReportProps> = ({ tabChange, excludeSupportUser }) => {
    const { hiddenColumns, handleToggleColumn } = useColumnVisibility({ pageName: 'expenseUsers' });
    const location = useLocation();
    const { roleID, timeZone } = useAppSelector(selectAuth);
    const showPresence = canWatchUserPresence(roleID);
    const viewerTimeZone = timeZone || 'UTC';

    const formatUtcInViewerTz = (utc?: string | Date | null) => {
        if (!utc) return ''
        const parsed = moment.utc(utc)
        if (!parsed.isValid()) return ''
        return parsed.tz(viewerTimeZone).format('MM-DD-YYYY hh:mm A')
    }

    const buildPresenceTooltipHtmlForRow = (row: any, presence: UserPresencePayload) => {
        const loginDateTime = formatUtcInViewerTz(row?.lastLoginDt)
        const leaveDateTime = formatUtcInViewerTz(presence.lastLeaveUtc || row?.lastLeaveDt)
        return buildPresenceTooltipHtml({
            isOnline: !!presence.isOnline,
            dateTimeLabel: presence.isOnline ? loginDateTime : leaveDateTime,
            sourceId: row?.lastLoginSourceId,
        })
    }
    const PAGE_INFO_SESSION_KEY = 'ss-userlist-info-dismissed'
    const [showPageInfo, setShowPageInfo] = useState(() => {
        try {
            return sessionStorage.getItem(PAGE_INFO_SESSION_KEY) !== '1'
        } catch {
            return true
        }
    })
    const dismissPageInfo = useCallback(() => {
        setShowPageInfo(false)
        try {
            sessionStorage.setItem(PAGE_INFO_SESSION_KEY, '1')
        } catch {
            /* ignore */
        }
    }, [])

    useEffect(() => {
        if (!showPageInfo) return
        const timer = window.setTimeout(() => {
            dismissPageInfo()
        }, 15000)
        return () => window.clearTimeout(timer)
    }, [showPageInfo, dismissPageInfo])

    const [presenceByUserId, setPresenceByUserId] = useState<Record<number, UserPresencePayload>>({});
    const [showAddUserModal, setShowAddUserModal] = useState(false)
    const { data: getAllOrganizationLookUp } = useGetAllOrganizationLookUpQuery('');
    const { data: designationLookUp } = useGetAllDesignationWithoutRoleQuery('');
    const { data: deparmentLookUp } = useDepartmentsLookupQuery('');
    const { data: roleLookUp } = useGetOrganizationAllRolesQuery('');
    const { data: getAllManagers } = useGetAllManagersLookupQuery('');
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10);
    const [searchStr, setSearchStr] = useState(''); // debounce search
    const [immediateSearchStr, setImmediateSearchStr] = useState('');
    const [sortBy, setSortBy] = useState("createDT");
    const [sortOrder, setSortOrder] = useState("desc");
    const [orgId, setOrgId] = useState(0);
    const [filterByDepartment, setFilterByDepartment] = useState('');
    const [filterByDesignation, setFilterByDesignation] = useState('');
    const [filterByRoleId, setFilterByRoleId] = useState("0");
    const [filterByStatus, setFilterByStatus] = useState("");
    const [sortedColumn, setSortedColumn] = useState("createDT");
    const [isChecked, setIsChecked] = useState(false)

    const [filterBySSId, setFilterBySSId] = useState("");
    const [localFilterBySSId, setLocalFilterBySSId] = useState("");
    const [filterByEmployeeId, setFilterByEmployeeId] = useState("");
    const [localFilterByEmployeeId, setLocalFilterByEmployeeId] = useState("");
    const [filterByBCEmployeeId, setFilterByBCEmployeeId] = useState("");
    const [localFilterByBCEmployeeId, setLocalFilterByBCEmployeeId] = useState("");
    const [filterByUserTypeId, setFilterByUserTypeId] = useState(0);
    const [localFilterByUserTypeId, setLocalFilterByUserTypeId] = useState(0);

    const [localOrgId, setLocalOrgId] = useState(0);
    const [localFilterByDepartment, setLocalFilterByDepartment] = useState('');
    const [localFilterByDesignation, setLocalFilterByDesignation] = useState('');
    const [localFilterByRoleId, setLocalFilterByRoleId] = useState("0");
    const [filterByExcludeSupportUser, setFilterByExcludeSupportUser] = useState('true')
    const [localFilterByExcludeSupportUser, setLocalFilterByExcludeSupportUser] = useState('true')
    const [localFilterByUserStatus, setLocalFilterByUserStatus] = useState('')

    const [filterByManager, setFilterByManager] = useState(0);
    const [localFilterByManager, setLocalFilterByManager] = useState(0);

    const [filterByOnlineStatus, setFilterByOnlineStatus] = useState('');
    const [localFilterByOnlineStatus, setLocalFilterByOnlineStatus] = useState('');
    const [lastLoginFrom, setLastLoginFrom] = useState('');
    const [lastLoginTo, setLastLoginTo] = useState('');
    const [localLastLoginFrom, setLocalLastLoginFrom] = useState('');
    const [localLastLoginTo, setLocalLastLoginTo] = useState('');
    const [localLastLoginValue, setLocalLastLoginValue] = useState('');

    const [isApplyingFilters, setIsApplyingFilters] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const [impersonateUserModal, setImpersonateUserModal] = useState(false);
    const [impersonateData, setImpersonateData] = useState({});
    const impersonateHandler = () => {
        setImpersonateUserModal((prev) => !prev);
    };


    const [userDetailByIdTrigger, { data: userDetailById }] = useLazyGetUserAccountDetailQuery({})
    const [getPermissionsListTrigger, { data: getPermissionsList }] = useLazyGetPermissionsListQuery({})
    const { data: allUserTypeLookup } = useGetAllUserTypeLookupQuery('')
    const [fetchPresenceSnapshot] = useLazyGetOrgUserLoggedInPresenceQuery()


    const { data: getAllUsersData,
        isFetching: getAllUsersIsLoading,
        isSuccess,
        refetch } = useGetAllUsersQuery({
            pageNumber: page,
            pageSize: pageSize,
            sortBy: sortBy,
            sortOrder: sortOrder,
            searchStr: searchStr,
            orgId: orgId,
            filterByDepartment: filterByDepartment,
            filterByDesignation: filterByDesignation,
            filterByRoleId: filterByRoleId,
            filterByStatus,
            ssId: filterBySSId,
            employeeId: filterByEmployeeId,
            bcEmployeeId: filterByBCEmployeeId,
            userTypeId: filterByUserTypeId,
            excludeSupportUser: filterByExcludeSupportUser,
            managerId: filterByManager,
            filterByOnlineStatus: filterByOnlineStatus || undefined,
            lastLoginFrom: lastLoginFrom || undefined,
            lastLoginTo: lastLoginTo || undefined,
        });

    const handlePageSizeChange = (e: any) => {
        const newSize = parseInt(e.target.value);
        setPageSize(newSize);
        setPage(1); // Reset page number to 1
    };

    // Seed / refresh presence from GetAllUserAccounts (isOnline / lastLeaveDt)
    useEffect(() => {
        if (!showPresence) return
        const rows = getAllUsersData?.result?.data
        if (!Array.isArray(rows)) return
        setPresenceByUserId((prev) => {
            const next = { ...prev }
            rows.forEach((u: any) => {
                const id = Number(u?.userAccountID)
                if (!id) return
                next[id] = {
                    orgUserId: id,
                    isOnline: !!u?.isOnline,
                    lastLeaveUtc: u?.lastLeaveDt || prev[id]?.lastLeaveUtc || null,
                    connectedOnUtc: prev[id]?.connectedOnUtc || null,
                    fullName: `${u?.firstName || ''} ${u?.lastName || ''}`.trim(),
                    email: u?.email,
                }
            })
            return next
        })
    }, [getAllUsersData, showPresence])

    useEffect(() => {
        if (!showPresence) return
        const unsub = subscribeUserPresence((payload) => {
            setPresenceByUserId((prev) => ({
                ...prev,
                [payload.orgUserId]: payload,
            }))
        })
        // HTTP snapshot fallback (covers hub reconnect / missed events)
        fetchPresenceSnapshot(undefined)
            .unwrap()
            .then((res: any) => {
                const rows = res?.result || res?.Result || []
                if (!Array.isArray(rows)) return
                setPresenceByUserId((prev) => {
                    const next = { ...prev }
                    rows.forEach((row: any) => {
                        const id = Number(row?.orgUserId ?? row?.OrgUserId)
                        if (!id) return
                        next[id] = {
                            orgUserId: id,
                            isOnline: !!(row?.isOnline ?? row?.IsOnline),
                            connectedOnUtc: row?.connectedOnUtc ?? row?.ConnectedOnUtc ?? null,
                            lastLeaveUtc: row?.lastLeaveUtc ?? row?.LastLeaveUtc ?? null,
                            fullName: row?.fullName ?? row?.FullName ?? null,
                            email: row?.email ?? row?.Email ?? null,
                        }
                    })
                    return next
                })
            })
            .catch(() => { /* presence is optional */ })
        return unsub
    }, [showPresence, fetchPresenceSnapshot])

    const resolvePresence = (userAccountId: number, row?: any): UserPresencePayload => {
        const live = presenceByUserId[userAccountId]
        if (live) {
            return {
                ...live,
                // Prefer live leave when offline; keep DB last leave while online (prior session)
                lastLeaveUtc: live.lastLeaveUtc || row?.lastLeaveDt || null,
            }
        }
        return {
            orgUserId: userAccountId,
            isOnline: !!row?.isOnline,
            lastLeaveUtc: row?.lastLeaveDt || null,
        }
    }

    const tableRef = useRef<HTMLTableElement>(null);
    const tableScrollRef = useRef<HTMLDivElement>(null);

    const [showUserDetailModal, setShowUserDetailModal] = useState(false)
    const [userDetailData, setuserDetailData] = useState({});
    const userDetailHandler = () => {
        setShowUserDetailModal((prev) => !prev);
    };


    const [downloading, setDownloading] = useState(false);
    const [excelReportTrigger, { isFetching: excelReportIsFetching }] = useLazyGetAllUsersExportReportQuery({})
    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blob = await excelReportTrigger({
                pageNumber: page,
                pageSize: 3000,
                sortBy: sortBy,
                sortOrder: sortOrder,
                searchStr: searchStr,
                orgId: orgId,
                filterByDepartment: filterByDepartment,
                filterByDesignation: filterByDesignation,
                filterByRoleId: filterByRoleId,
                filterByStatus,
                ssId: filterBySSId,
                employeeId: filterByEmployeeId,
                bcEmployeeId: filterByBCEmployeeId,
                userTypeId: filterByUserTypeId,
                excludeSupportUser: filterByExcludeSupportUser,
                managerId: filterByManager,
                filterByOnlineStatus: filterByOnlineStatus || undefined,
                lastLoginFrom: lastLoginFrom || undefined,
                lastLoginTo: lastLoginTo || undefined,
            }).unwrap();

            if (blob) {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "Expense_Report.xlsx"; // 👈 filename
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            }
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            setDownloading(false);
        }
    };

    const [showUpdateUserModal, setShowUpdateUserModal] = useState(false)
    const [editData, setEditData] = useState({});
    const editUserHandler = () => {
        setShowUpdateUserModal((prev) => !prev);
    };
    const [showConfirmationActiveUserModal, setShowConfirmationActiveUserModal] = useState(false)
    const expenseConfirmationModalHandler = () => {
        setShowConfirmationActiveUserModal((prev) => !prev);
    };
    const [showUpdateUserEmailModal, setShowUpdateUserEmailModal] = useState(false)
    const updateUserEmailHandler = () => {
        setShowUpdateUserEmailModal((prev) => !prev);
    };
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false)
    const passwordResetModalHandler = () => {
        setShowPasswordResetModal((prev) => !prev);
    };
    // pagination
    const handlePageClick = (e: any) => {
        setPage(e.selected + 1);
    };

    const handleSort = (property: string) => {
        setSortBy(property);
        if (sortedColumn === property) {
            setSortOrder(prevSortOrder => (prevSortOrder === "asc" ? "desc" : "asc"));
        } else {
            setSortedColumn(property);
            setSortOrder("asc");
        }
        if (property === 'active') {
            setFilterByStatus('');
            setLocalFilterByUserStatus('');
        }
        // Perform additional sorting logic...
    };

    //per page record
    const handlePageRecords = (e: any) => {
        setPage(1); // Reset the page to 1
    };
    const handleSearchChange = (e: any) => {
        const { value } = e.target;
        setImmediateSearchStr(value); // Update immediate search state

        delayedSearch(value); // Update debounced search state
        setPage(1)
    };

    const delayedSearch = useCallback(
        debounce((searchValue) => {
            setSearchStr(searchValue);
        }, 500),
        []
    );

    const handleOrgChange = (selectedOption: any) => {
        setLocalOrgId(selectedOption?.value ?? 0);
    };

    const handleDeparmentChange = (selectedOption: any) => {
        setLocalFilterByDepartment(selectedOption?.value ?? '');
    };

    const handleDesignationChange = (selectedOption: any) => {
        setLocalFilterByDesignation(selectedOption?.value ?? '');
    };

    const handleRoleChange = (selectedOption: any) => {
        setLocalFilterByRoleId(selectedOption?.value ?? '0');
    };

    const handleFilterBySSId = (e: any) => {
        setLocalFilterBySSId(e.target.value);
    };

    const handleFilterByEmployeeId = (e: any) => {
        setLocalFilterByEmployeeId(e.target.value);
    };
    const handleFilterByBCEmployeeId = (e: any) => {
        setLocalFilterByBCEmployeeId(e.target.value);
    };
    const handleFilterByUserTypeId = (selectedOption: any) => {
        setLocalFilterByUserTypeId(selectedOption?.value ?? 0);
    };
    const handleFilterByExcludeSupportUser = (selectedOption: any) => {
        setLocalFilterByExcludeSupportUser(selectedOption?.value ?? 'true');
    };
    const handleFilterByUserStatus = (selectedOption: any) => {
        setLocalFilterByUserStatus(selectedOption?.value ?? '');
    };

    const handleFilterByManager = (selectedOption: any) => {
        setLocalFilterByManager(selectedOption?.value ?? 0);
    };

    const handleFilterByOnlineStatus = (selectedOption: any) => {
        setLocalFilterByOnlineStatus(selectedOption?.value ?? '');
    };

    const handleLastLoginDateApply = (_event: any, picker: any) => {
        const from = picker.startDate.format('YYYY-MM-DD');
        const to = picker.endDate.format('YYYY-MM-DD');
        setLocalLastLoginFrom(from);
        setLocalLastLoginTo(to);
        setLocalLastLoginValue(`${picker.startDate.format('MM/DD/YYYY')} - ${picker.endDate.format('MM/DD/YYYY')}`);
    };

    const handleLastLoginDateCancel = () => {
        setLocalLastLoginFrom('');
        setLocalLastLoginTo('');
        setLocalLastLoginValue('');
    };

    const debouncedRefetch = debounce(() => {
        refetch().finally(() => {
            setIsApplyingFilters(false);
        });
    }, 300);
    useEffect(() => {
        if (isApplyingFilters) {
            debouncedRefetch();
        }
    }, [isApplyingFilters]);
    const applyFilters = () => {
        setIsApplyingFilters(true);
        setOrgId(localOrgId);
        setFilterByDepartment(localFilterByDepartment);
        setFilterByDesignation(localFilterByDesignation);
        setFilterByRoleId(localFilterByRoleId);
        setFilterBySSId(localFilterBySSId);
        setFilterByEmployeeId(localFilterByEmployeeId);
        setFilterByBCEmployeeId(localFilterByBCEmployeeId);
        setFilterByUserTypeId(localFilterByUserTypeId);
        setFilterByExcludeSupportUser(localFilterByExcludeSupportUser);
        setFilterByManager(localFilterByManager);
        // setLocalFilterByUserStatus(localFilterByUserStatus)
        setFilterByStatus(localFilterByUserStatus);
        setFilterByOnlineStatus(localFilterByOnlineStatus);
        setLastLoginFrom(localLastLoginFrom);
        setLastLoginTo(localLastLoginTo);

        setPage(1)
    };
    useEffect(() => {
        if (searchStr === "" ||
            sortOrder === "" ||
            sortBy === "") {
            refetch();
        }
    }, [searchStr, sortOrder, sortBy]);
    useEffect(() => {
        if (tabChange === 'first') {
            refetch();
        }
        refetch()
    }, [tabChange]);
    const [pageTitle, setPageTitle] = useState('User List');
    useEffect(() => {
        // Check the current route and set the selected value accordingly
        if (location.pathname === '/expense-users/managers') {
            setFilterByRoleId('4');
            setPageTitle('All Managers');
            setFilterByStatus('');
            setLocalFilterByUserStatus('');
        }
        else if (location.pathname === '/active/managers') {
            setFilterByRoleId('4');
            setFilterByStatus('active')
            setLocalFilterByUserStatus('Active');
            setPageTitle('Active Manager');
        }
        else if (location.pathname === '/all/expense-users') {
            setFilterByRoleId('5');
            setPageTitle('All Users');
            setFilterByStatus('');
            setLocalFilterByUserStatus('');
        }
        else if (location.pathname === '/active/expense-users') {
            setFilterByRoleId('5');
            setFilterByStatus('active')
            setLocalFilterByUserStatus('Active');
            setPageTitle('Active Users');
        }
        else {
            setFilterByRoleId('0')
            setPageTitle('User List');
            setFilterByStatus('');
            setLocalFilterByUserStatus('');
        }
    }, [location.pathname]);

    const resetFilters = () => {
        setOrgId(0)
        // setFilterByRoleId("0")
        setFilterByDepartment("")
        setFilterByDesignation("")
        setLocalOrgId(0);
        setLocalFilterByDepartment('');
        setLocalFilterByDesignation('');
        setFilterBySSId('');
        setLocalFilterBySSId('');
        setFilterByEmployeeId('');
        setLocalFilterByEmployeeId('');
        setFilterByBCEmployeeId('');
        setLocalFilterByBCEmployeeId('');
        setLocalFilterByUserTypeId(0);
        setFilterByUserTypeId(0);
        setFilterByExcludeSupportUser('false');
        setLocalFilterByExcludeSupportUser('false');
        setFilterByManager(0);
        setLocalFilterByManager(0);
        setLocalFilterByUserStatus('');
        setFilterByStatus('');
        setFilterByOnlineStatus('');
        setLocalFilterByOnlineStatus('');
        setLastLoginFrom('');
        setLastLoginTo('');
        setLocalLastLoginFrom('');
        setLocalLastLoginTo('');
        setLocalLastLoginValue('');
        // setLocalFilterByRoleId("0");
    }

    // Tab state and handler
    const [activeTab, setActiveTab] = useState('0');
    const handleTabChange = (status: string) => {
        setActiveTab(status);
        setFilterByRoleId(status);
        setLocalFilterByRoleId(status);
        setPage(1)
        setPageSize(10)
    };
    const handleRefresh = () => {
        refetch()
            .then(() => {
                if (isSuccess) {
                    toast.success(tosterKey.dataRefreshedSuccesfully);
                }
            });
    };
    return (
        <>
            <ImpersonateUserModal show={impersonateUserModal} handleClose={() => setImpersonateUserModal(false)} data={impersonateData} />
            <UpdateUserModal show={showUpdateUserModal} handleClose={() => setShowUpdateUserModal(false)}
                data={userDetailById?.result}
                refetch={refetch} />
            <UpdateUserEmailModal show={showUpdateUserEmailModal} handleClose={() => setShowUpdateUserEmailModal(false)}
                data={editData}
                refetch={refetch} />
            <PasswordResetModal show={showPasswordResetModal} handleClose={() => setShowPasswordResetModal(false)}
                data={editData}
                refetch={refetch} />
            <UserDetailModal show={showUserDetailModal} handleClose={() => setShowUserDetailModal(false)} data={userDetailData} />
            <AddUserModal
                show={showAddUserModal}
                handleClose={() => setShowAddUserModal(false)}
                data={getPermissionsList}
                refetch={refetch} />
            <UserActiveConfirmation
                show={showConfirmationActiveUserModal}
                handleClose={() => setShowConfirmationActiveUserModal(false)}
                data={editData}
                isChecked={isChecked}
                refetch={refetch} />

            <PageTitle breadcrumbs={[]}>
                {roleID === constraintConfig.roleID.role1 ?
                    "Admin Users List" :
                    pageTitle}
            </PageTitle>

            <div className='card'>
                <div className='card-header border-0 align-items-center flex-wrap gap-2 pt-5'>
                        {showPageInfo && (
                            <Alert
                                variant="primary"
                                className="mt-2 mb-0 py-2 ss-page-info-alert ss-page-info-alert--full"
                            >
                                <div className="ss-page-info-alert__body">
                                    <p className='fs-7 mb-0'>
                                        Displays all registered users along with their organizational information, including roles, employee IDs, managers, office locations, account status, and user types. This page helps administrators review and manage user information based on the selected filters.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-icon btn-active-color-primary ss-page-info-alert__close"
                                    aria-label="Close"
                                    onClick={dismissPageInfo}
                                >
                                    <i className="fas fa-times" />
                                </button>
                            </Alert>
                        )}
                        <div className='d-flex flex-column flex-lg-row flex-wrap align-items-stretch align-items-lg-center justify-content-between gap-2 w-100 ss-user-list-toolbar ss-receipt-vault-toolbar'>
                            <div className='d-flex gap-2 align-items-center min-w-0 w-100 w-lg-auto ss-receipt-vault-toolbar__tabs'>
                                <div className='ss-mobile-scroll-tabs flex-grow-1 flex-lg-grow-0 min-w-0'>
                                    <div className="ss-user-tab-group btn-group" role="group" aria-label="User role tabs" style={{ minWidth: 'max-content' }}>
                                    <button
                                        type='button'
                                        className={`btn btn-sm text-nowrap ${activeTab === '0' ? 'btn-primary' : 'btn-light-primary'}`}
                                        onClick={() => handleTabChange('0')}
                                    >
                                        {labelKey.all}
                                    </button>
                                    {(roleLookUp?.result || [])
                                        .filter((option: any) => {
                                            if (excludeSupportUser === true && option.value === "Org Admin") {
                                                return false;
                                            }
                                            if (roleID === constraintConfig.roleID.role1) {
                                                return option.id === 1 || option.id === 2 || option.id === 13;
                                            }
                                            if (roleID === constraintConfig.roleID.role2) {
                                                return option.id >= 2;
                                            }
                                            if (roleID === constraintConfig.roleID.roleExecutive) {
                                                return option.id === 4 || option.id === 5;
                                            }
                                            return true;
                                        })
                                        .map((options: any) => (
                                            <button
                                                key={options.id}
                                                type='button'
                                                className={`btn btn-sm text-nowrap ${activeTab === options.id ? 'btn-primary' : 'btn-light-primary'}`}
                                                onClick={() => handleTabChange(options.id)}
                                            >
                                                {options.value}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className='ss-mobile-scroll-tabs w-100 w-lg-auto ss-receipt-vault-toolbar__actions ms-lg-auto'>
                                <div className='d-flex justify-content-start align-items-center flex-nowrap gap-2 ms-lg-auto ss-user-list-toolbar__actions' data-kt-user-table-toolbar='base' style={{ minWidth: 'max-content' }}>
                                <SearchBarComponent
                                    placeholder='User'
                                    value={immediateSearchStr}
                                    onChange={handleSearchChange}
                                    className='ss-user-toolbar-search-input'
                                    mainClassName='ss-table-search--sm my-0'
                                />
                                <RefreshComponent onClick={handleRefresh} />
                                {!showFilters ? (
                                    <button
                                        type="button"
                                        className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active"
                                        onClick={() => setShowFilters(true)}
                                    >
                                        <i className="fa fa-filter me-1 text-white" />
                                        Show Filters
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-sm ss-pill-btn ss-pill-btn-filter-active"
                                        onClick={() => setShowFilters(false)}
                                    >
                                        <i className="fa fa-eye-slash me-1 text-white" />
                                        Hide Filters
                                    </button>
                                )}

                                {excludeSupportUser && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-info ss-smart-btn"
                                        onClick={handleDownload}
                                        disabled={downloading || excelReportIsFetching}
                                    >
                                        <i className="fa fa-download me-1 text-white" />
                                        {downloading || excelReportIsFetching ? "Exporting User Report" : "Export User Report"}
                                    </button>
                                )}
                                {excludeSupportUser !== true && (
                                    <button type='button' className='btn btn-sm ss-pill-btn ss-pill-btn-filter-active'
                                        onClick={() => {
                                            setShowAddUserModal(true)
                                            getPermissionsListTrigger({})
                                        }}>
                                        <i className="fa fa-plus me-1 text-white" />
                                        Add Expense User
                                    </button>
                                )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='card-body py-3'>
                        {showFilters && (
                        <div className='ss-user-list-filters mb-3'>
                            <div className='row g-2 ss-listing-filters align-items-end'>
                                {(roleID === constraintConfig.roleID.role1) && (
                                    <div className='col-md-4 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>{labelKey.organization}</label>
                                        <Select
                                            className='ss-compact-select filter-valueContainer'
                                            classNamePrefix='react-select'
                                            options={(Array.isArray(getAllOrganizationLookUp?.result)
                                                ? getAllOrganizationLookUp.result
                                                : Array.isArray(getAllOrganizationLookUp)
                                                    ? getAllOrganizationLookUp
                                                    : []
                                            ).map((option: any) => ({
                                                value: option.id,
                                                label: option?.value,
                                            }))}
                                            placeholder='Select Organization'
                                            value={
                                                localOrgId
                                                    ? {
                                                        value: localOrgId,
                                                        label:
                                                            getAllOrganizationLookUp?.result?.find(
                                                                (option: any) => option.id === localOrgId
                                                            )?.value || 'Select Organization',
                                                    }
                                                    : null
                                            }
                                            onChange={handleOrgChange}
                                            isClearable
                                        />
                                    </div>
                                )}
                                <div className='col-md-4 col-lg-3 col-xl-2'>
                                    <label className='form-label ss-compact-filter-label'>{labelKey.department}</label>
                                    <Select
                                        className='ss-compact-select filter-valueContainer'
                                        classNamePrefix='react-select'
                                        options={(Array.isArray(deparmentLookUp?.result)
                                            ? deparmentLookUp.result
                                            : Array.isArray(deparmentLookUp)
                                                ? deparmentLookUp
                                                : []
                                        ).map((option: any) => ({
                                            value: option.value,
                                            label: option.value,
                                        }))}
                                        placeholder='Select Department'
                                        value={
                                            localFilterByDepartment
                                                ? { value: localFilterByDepartment, label: localFilterByDepartment }
                                                : null
                                        }
                                        onChange={handleDeparmentChange}
                                        isClearable
                                    />
                                </div>
                                <div className='col-md-4 col-lg-3 col-xl-2'>
                                    <label className='form-label ss-compact-filter-label'>{labelKey.designation}</label>
                                    <Select
                                        className='ss-compact-select filter-valueContainer'
                                        classNamePrefix='react-select'
                                        options={(Array.isArray(designationLookUp?.result)
                                            ? designationLookUp.result
                                            : Array.isArray(designationLookUp)
                                                ? designationLookUp
                                                : []
                                        ).map((option: any) => ({
                                            value: option?.value ?? option?.id ?? option,
                                            label: option?.value ?? option?.name ?? option,
                                        }))}
                                        placeholder='Select Designation'
                                        value={
                                            localFilterByDesignation
                                                ? { value: localFilterByDesignation, label: localFilterByDesignation }
                                                : null
                                        }
                                        onChange={handleDesignationChange}
                                        isClearable
                                    />
                                </div>
                                <div className='col-md-4 col-lg-3 col-xl-2'>
                                    <label className='form-label ss-compact-filter-label'>{labelKey.userType}</label>
                                    <Select
                                        className='ss-compact-select filter-valueContainer'
                                        classNamePrefix='react-select'
                                        options={(Array.isArray(allUserTypeLookup?.result) ? allUserTypeLookup.result : []).map(
                                            (option: any) => ({
                                                value: option.id,
                                                label: option?.value,
                                            })
                                        )}
                                        placeholder='Select User Type'
                                        value={
                                            localFilterByUserTypeId
                                                ? {
                                                    value: localFilterByUserTypeId,
                                                    label:
                                                        allUserTypeLookup?.result?.find(
                                                            (option: any) => option.id === localFilterByUserTypeId
                                                        )?.value || 'Select User Type',
                                                }
                                                : null
                                        }
                                        onChange={handleFilterByUserTypeId}
                                        isClearable
                                    />
                                </div>
                                {excludeSupportUser !== true && (
                                    <div className='col-md-4 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>{labelKey.excludeSupportUser}</label>
                                        <Select
                                            className='ss-compact-select filter-valueContainer'
                                            classNamePrefix='react-select'
                                            options={[
                                                { value: 'true', label: 'Yes' },
                                                { value: 'false', label: 'No' },
                                            ]}
                                            placeholder='Exclude Support User'
                                            value={
                                                localFilterByExcludeSupportUser != null
                                                    ? {
                                                        value: localFilterByExcludeSupportUser,
                                                        label: localFilterByExcludeSupportUser === 'true' ? 'Yes' : 'No',
                                                    }
                                                    : { value: 'true', label: 'Yes' }
                                            }
                                            onChange={handleFilterByExcludeSupportUser}
                                        />
                                    </div>
                                )}
                                <div className='col-md-4 col-lg-3 col-xl-2'>
                                    <label className='form-label ss-compact-filter-label'>{labelKey.status}</label>
                                    <Select
                                        className='ss-compact-select filter-valueContainer'
                                        classNamePrefix='react-select'
                                        options={[
                                            { value: 'active', label: 'Active' },
                                            { value: 'inactive', label: 'InActive' },
                                        ]}
                                        placeholder='Select Status'
                                        value={
                                            localFilterByUserStatus
                                                ? {
                                                    value: localFilterByUserStatus === 'Active' || localFilterByUserStatus === 'active' ? 'active' : localFilterByUserStatus === 'InActive' || localFilterByUserStatus === 'inactive' ? 'inactive' : localFilterByUserStatus,
                                                    label:
                                                        localFilterByUserStatus === 'active' || localFilterByUserStatus === 'Active'
                                                            ? 'Active'
                                                            : localFilterByUserStatus === 'inactive' || localFilterByUserStatus === 'InActive'
                                                                ? 'InActive'
                                                                : localFilterByUserStatus,
                                                }
                                                : null
                                        }
                                        onChange={handleFilterByUserStatus}
                                        isClearable
                                    />
                                </div>
                                {showPresence && (
                                    <div className='col-md-4 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>Online Status</label>
                                        <Select
                                            className='ss-compact-select filter-valueContainer'
                                            classNamePrefix='react-select'
                                            options={[
                                                { value: 'online', label: 'Online' },
                                                { value: 'offline', label: 'Offline' },
                                            ]}
                                            placeholder='Online Status'
                                            value={
                                                localFilterByOnlineStatus
                                                    ? {
                                                        value: localFilterByOnlineStatus,
                                                        label: localFilterByOnlineStatus === 'online' ? 'Online' : 'Offline',
                                                    }
                                                    : null
                                            }
                                            onChange={handleFilterByOnlineStatus}
                                            isClearable
                                        />
                                    </div>
                                )}
                            </div>
                            <div className='row g-2 mt-1 ss-listing-filters align-items-end'>
                                {excludeSupportUser !== true && (
                                    <div className='col-md-4 col-lg-3 col-xl-2'>
                                        <label className='form-label ss-compact-filter-label'>SS ID</label>
                                        <input
                                            type='text'
                                            className='form-control ss-compact-input'
                                            placeholder='Search By SSID'
                                            value={localFilterBySSId}
                                            onChange={handleFilterBySSId}
                                        />
                                    </div>
                                )}
                                <div className='col-md-4 col-lg-3 col-xl-2'>
                                    <label className='form-label ss-compact-filter-label'>Employee ID</label>
                                    <input
                                        type='text'
                                        className='form-control ss-compact-input'
                                        placeholder='Search By Employee ID'
                                        value={localFilterByEmployeeId}
                                        onChange={handleFilterByEmployeeId}
                                    />
                                </div>
                                <div className='col-md-4 col-lg-3 col-xl-2'>
                                    <label className='form-label ss-compact-filter-label'>BC Employee ID</label>
                                    <input
                                        type='text'
                                        className='form-control ss-compact-input'
                                        placeholder='Search By BC Employee ID'
                                        value={localFilterByBCEmployeeId}
                                        onChange={handleFilterByBCEmployeeId}
                                    />
                                </div>
                                <div className='col-md-4 col-lg-3 col-xl-2'>
                                    <label className='form-label ss-compact-filter-label'>{labelKey.manager}</label>
                                    <Select
                                        className='ss-compact-select filter-valueContainer'
                                        classNamePrefix='react-select'
                                        options={[
                                            { value: 0, label: 'All Managers' },
                                            ...(Array.isArray(getAllManagers?.result)
                                                ? getAllManagers.result
                                                : Array.isArray(getAllManagers)
                                                    ? getAllManagers
                                                    : []
                                            ).map((option: any) => ({
                                                value: option.id,
                                                label: option?.value,
                                            })),
                                        ]}
                                        placeholder='Select Manager'
                                        value={
                                            localFilterByManager
                                                ? {
                                                    value: localFilterByManager,
                                                    label:
                                                        getAllManagers?.result?.find(
                                                            (option: any) => option.id === localFilterByManager
                                                        )?.value || 'Select Manager',
                                                }
                                                : { value: 0, label: 'All Managers' }
                                        }
                                        onChange={handleFilterByManager}
                                        isClearable
                                    />
                                </div>
                                <div className='col-auto ss-user-list-login-actions'>
                                    <div className='d-flex align-items-end gap-2 flex-nowrap'>
                                        <div className='ss-compact-date-field ss-user-list-last-login-field'>
                                            <DateRangeComp
                                                label='Last Login Date Range'
                                                startDate={localLastLoginFrom ? moment(localLastLoginFrom) : undefined}
                                                endDate={localLastLoginTo ? moment(localLastLoginTo) : undefined}
                                                value={localLastLoginValue}
                                                onApply={handleLastLoginDateApply}
                                                onCancel={handleLastLoginDateCancel}
                                            />
                                        </div>
                                        <div className='d-flex gap-2 ss-listing-filter-actions ss-user-list-filter-actions pb-0'>
                                            <button type='button' className='btn btn-sm btn-danger ss-smart-btn text-nowrap' onClick={resetFilters}>
                                                Reset
                                            </button>
                                            <button type='button' className='btn btn-sm btn-primary ss-smart-btn text-nowrap' onClick={applyFilters}>
                                                {isApplyingFilters ? (
                                                    <>
                                                        <span className='me-2'>Loading...</span>
                                                        <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
                                                    </>
                                                ) : (
                                                    'Apply Filters'
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}

                        <Tooltip id="Reset-Password" place="left" positionStrategy="fixed" />
                        <Tooltip id="impersonate" place="left" positionStrategy="fixed" />
                        <Tooltip id="update-user" place="left" positionStrategy="fixed" />
                        <Tooltip id="user-detail" place="left" positionStrategy="fixed" />
                        <Tooltip
                            id="user-presence"
                            place="top"
                            positionStrategy="fixed"
                            openOnClick
                            clickable
                            closeOnScroll
                            opacity={1}
                            className="ss-presence-tooltip-host"
                        />
                        <Tooltip id="user-login-source" place="top" positionStrategy="fixed" openOnClick closeOnScroll />
                        <div className='table-responsive table-height' ref={tableScrollRef}>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 ss-user-list-table ss-smart-table' ref={tableRef}>
                                    <thead>
                                        <tr className='fw-bold text-muted'>
                                            {hiddenColumns.includes('sr') && (
                                                <TableHeading
                                                    label={labelKey.sr}
                                                    columnId='sr'
                                                    className='min-w-40px ps-4'
                                                />
                                            )}
                                            {hiddenColumns.includes('firstName') && (
                                                <TableHeading
                                                    label={labelKey.name}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='firstName'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('userOrg') && (
                                                <TableHeading
                                                    label={labelKey.organization}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='userOrg'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('ssId') && (
                                                <TableHeading
                                                    label={labelKey.ssId}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='ssId'
                                                    className='min-w-120px'
                                                />
                                            )}

                                            {hiddenColumns.includes('employeeId') && (
                                                <TableHeading
                                                    label={labelKey.employeeId}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='employeeId'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('bcEmployeeId') && (
                                                <TableHeading
                                                    label={labelKey.bcEmployeeId}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='bcEmployeeId'
                                                    className='min-w-120px'
                                                />
                                            )}

                                            {hiddenColumns.includes('userRole') && (
                                                <TableHeading
                                                    label={labelKey.role}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='userRole'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('userType') && (
                                                <TableHeading
                                                    label={labelKey.userType}
                                                    columnId='userType'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('manager') && (
                                                <TableHeading
                                                    label={labelKey.manager}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='manager'
                                                />
                                            )}
                                            {hiddenColumns.includes('delegateManager') && (
                                                <TableHeading
                                                    label={labelKey.delegateManager}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='delegateManager'
                                                />
                                            )}
                                            {hiddenColumns.includes('mgrApproveMaxLimit') && (
                                                <TableHeading
                                                    label={labelKey.managerMaxLimit}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='mgrApproveMaxLimit'
                                                />
                                            )}
                                            {hiddenColumns.includes('location') && (
                                                <TableHeading
                                                    label={labelKey.location}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='location'
                                                    className='min-w-120px'
                                                />
                                            )}

                                            {hiddenColumns.includes('department') && (
                                                <TableHeading
                                                    label={labelKey.department}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='department'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('designation') && (
                                                <TableHeading
                                                    label={labelKey.designation}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='designation'
                                                    className='min-w-120px'
                                                />
                                            )}

                                            {hiddenColumns.includes('officeLocation') && (
                                                <TableHeading
                                                    label={labelKey.officeLocation}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='officeLocation'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('homeLocation') && (
                                                <TableHeading
                                                    label={labelKey.homeLocation}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='homeLocation'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('active') && (
                                                <TableHeading
                                                    label={labelKey.status}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='active'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('createDT') && (
                                                <TableHeading
                                                    label={labelKey.createdDate}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='createDT'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('createdBy') && (
                                                <TableHeading
                                                    label={labelKey.createdBy}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='createdBy'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('modifiedOn') && (
                                                <TableHeading
                                                    label='Modified Date'
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='modifiedOn'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('modifiedBy') && (
                                                <TableHeading
                                                    label={labelKey.modifiedBy}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='modifiedBy'
                                                    className='min-w-120px'
                                                />
                                            )}
                                            {hiddenColumns.includes('lastLoginDt') && (
                                                <TableHeading
                                                    label={`${labelKey.lastLogin} (Source)`}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='lastLoginDt'
                                                    className='min-w-175px'
                                                />
                                            )}
                                            {hiddenColumns.includes('lastLeaveDt') && (
                                                <TableHeading
                                                    label={`${labelKey.lastLeave} (Source)`}
                                                    sortedColumn={sortedColumn}
                                                    sortOrder={sortOrder}
                                                    handleSort={handleSort}
                                                    columnId='lastLeaveDt'
                                                    className='min-w-175px'
                                                />
                                            )}
                                            <th className='text-end rounded-end pe-2 border-0 sticky-table-actions'>
                                                <div className='d-flex align-items-center justify-content-end'>
                                                {hiddenColumns.includes('actions') && excludeSupportUser !== true && (
                                                    <span className='me-2'>{labelKey.actions}</span>
                                                )}
                                                <TableSettingMenu>
                                                    <TextField
                                                        rightLabel={labelKey.sr}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("sr")}
                                                        checked={hiddenColumns.includes('sr')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.name}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("firstName")}
                                                        checked={hiddenColumns.includes('firstName')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.organization}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("userOrg")}
                                                        checked={hiddenColumns.includes('userOrg')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.ssId}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("ssId")}
                                                        checked={hiddenColumns.includes('ssId')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.employeeId}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("employeeId")}
                                                        checked={hiddenColumns.includes('employeeId')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.bcEmployeeId}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("bcEmployeeId")}
                                                        checked={hiddenColumns.includes('bcEmployeeId')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.role}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("userRole")}
                                                        checked={hiddenColumns.includes('userRole')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.userType}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("userType")}
                                                        checked={hiddenColumns.includes('userType')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.manager}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("manager")}
                                                        checked={hiddenColumns.includes('manager')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.delegateManager}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("delegateManager")}
                                                        checked={hiddenColumns.includes('delegateManager')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.managerMaxLimit}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("mgrApproveMaxLimit")}
                                                        checked={hiddenColumns.includes('mgrApproveMaxLimit')}
                                                        fieldClass='mb-4'
                                                    />

                                                    <TextField
                                                        rightLabel={labelKey.department}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("department")}
                                                        checked={hiddenColumns.includes('department')}
                                                        fieldClass='mb-4'
                                                    />

                                                    <TextField
                                                        rightLabel={labelKey.designation}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("designation")}
                                                        checked={hiddenColumns.includes('designation')}
                                                        fieldClass='mb-4'
                                                    />

                                                    <TextField
                                                        rightLabel={labelKey.officeLocation}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("officeLocation")}
                                                        checked={hiddenColumns.includes('officeLocation')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel="Home Location"
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("homeLocation")}
                                                        checked={hiddenColumns.includes('homeLocation')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.active}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("active")}
                                                        checked={hiddenColumns.includes('active')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.createdDate}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("createDT")}
                                                        checked={hiddenColumns.includes('createDT')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.createdBy}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("createdBy")}
                                                        checked={hiddenColumns.includes('createdBy')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.modifiedOn}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("modifiedOn")}
                                                        checked={hiddenColumns.includes('modifiedOn')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.modifiedBy}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("modifiedBy")}
                                                        checked={hiddenColumns.includes('modifiedBy')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.lastLogin}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("lastLoginDt")}
                                                        checked={hiddenColumns.includes('lastLoginDt')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.lastLeave}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("lastLeaveDt")}
                                                        checked={hiddenColumns.includes('lastLeaveDt')}
                                                        fieldClass='mb-4'
                                                    />
                                                    <TextField
                                                        rightLabel={labelKey.actions}
                                                        type="checkbox"
                                                        onChange={() => handleToggleColumn("actions")}
                                                        checked={hiddenColumns.includes('actions')}
                                                        fieldClass='mb-4'
                                                    />
                                                </TableSettingMenu>
                                                </div>
                                            </th>

                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!getAllUsersIsLoading &&
                                            <>
                                                {getAllUsersData?.result?.data?.length > 0 ? (
                                                    <>
                                                        {getAllUsersData?.result?.data?.map((data: any, index: any) => (
                                                            <tr key={index}>
                                                                {hiddenColumns.includes('sr') && (
                                                                    <td className='ps-4'>
                                                                        {(page - 1) * pageSize + index + 1}
                                                                    </td>
                                                                )}
                                                                {hiddenColumns.includes('firstName') && (
                                                                    <td>
                                                                        {((roleID === constraintConfig.roleID.role1 || roleID === constraintConfig.roleID.role2) ? (
                                                                            <div className="d-flex align-items-center">
                                                                                <div className="symbol symbol-circle symbol-40px overflow-hidden me-3 position-relative ss-user-list-avatar" style={{ width: '40px', height: '40px', minWidth: '40px', minHeight: '40px' }}>
                                                                                    {data?.imageUrl ? <img src={data?.imageUrl} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover' }} /> :
                                                                                        <div className="symbol-label fs-3 text-capitalize">{data?.firstName ? data.firstName.charAt(0) : ''}</div>
                                                                                    }
                                                                                    {showPresence && (() => {
                                                                                        const presence = resolvePresence(data.userAccountID, data)
                                                                                        return (
                                                                                            <UserPresenceDot
                                                                                                isOnline={!!presence.isOnline}
                                                                                                tooltipHtml={buildPresenceTooltipHtmlForRow(data, presence)}
                                                                                            />
                                                                                        )
                                                                                    })()}
                                                                                </div>
                                                                                <Link to={`/user-profile/expense-items/${data.userAccountID}`} className="d-flex flex-column text-decoration-none">
                                                                                    <p className="text-gray-800 fw-bold text-hover-primary mb-1 text-capitalize d-flex align-items-center gap-2">
                                                                                        {data?.firstName} {data?.lastName}
                                                                                    </p>
                                                                                    <span className='text-lowercase'>{data?.email}</span>
                                                                                </Link>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="d-flex align-items-center">
                                                                                <div className="symbol symbol-circle symbol-40px overflow-hidden me-3 position-relative ss-user-list-avatar" style={{ width: '40px', height: '40px', minWidth: '40px', minHeight: '40px' }}>
                                                                                    {data?.imageUrl ? <img src={data?.imageUrl} alt="" style={{ width: '40px', height: '40px', objectFit: 'cover' }} /> :
                                                                                        <div className="symbol-label fs-3 text-capitalize">{data?.firstName ? data.firstName.charAt(0) : ''}</div>
                                                                                    }
                                                                                    {showPresence && (() => {
                                                                                        const presence = resolvePresence(data.userAccountID, data)
                                                                                        return (
                                                                                            <UserPresenceDot
                                                                                                isOnline={!!presence.isOnline}
                                                                                                tooltipHtml={buildPresenceTooltipHtmlForRow(data, presence)}
                                                                                            />
                                                                                        )
                                                                                    })()}
                                                                                </div>
                                                                                <div className="d-flex flex-column">
                                                                                    <span className="text-gray-800 fw-bold mb-1 text-capitalize">{data?.firstName} {data?.middleName} {data?.lastName}</span>
                                                                                    <span className='text-lowercase'>{data?.email}</span>
                                                                                </div>
                                                                            </div>
                                                                        ))}

                                                                    </td>
                                                                )}
                                                                {hiddenColumns.includes('userOrg') && (
                                                                    <td>{data?.userOrg?.userInOrgName ? data?.userOrg?.userInOrgName : "_"}</td>
                                                                )}
                                                                {hiddenColumns.includes('ssId') && (
                                                                    <td>{data?.userAccountID}</td>
                                                                )}
                                                                {hiddenColumns.includes('employeeId') && (
                                                                    <td>{data?.employeeId}</td>
                                                                )}
                                                                {hiddenColumns.includes('bcEmployeeId') && (
                                                                    <td>{data?.bcEmployeeId}</td>
                                                                )}
                                                                {hiddenColumns.includes('userRole') && (
                                                                    <td>{data?.userRole?.roleName}</td>
                                                                )}
                                                                {hiddenColumns.includes('userType') && (
                                                                    <td>{data?.userType?.value}</td>
                                                                )}
                                                                {hiddenColumns.includes('manager') && (
                                                                    <td>{data?.managerName}</td>
                                                                )}
                                                                {hiddenColumns.includes('delegateManager') && (
                                                                    <td>{data?.delegateManager?.value}</td>
                                                                )}
                                                                {hiddenColumns.includes('mgrApproveMaxLimit') && (
                                                                    <td><CurrencyComp amount={data?.mgrApproveMaxLimit} /></td>
                                                                )}
                                                                {hiddenColumns.includes('location') && (
                                                                    <td>{data?.location?.value}</td>
                                                                )}
                                                                {hiddenColumns.includes('department') && (
                                                                    <td>{data?.department?.title ? data?.department?.title : "N/A"}</td>
                                                                )}

                                                                {hiddenColumns.includes('designation') && (
                                                                    <td>{data?.designation?.title ? data?.designation?.title : "N/A"}</td>
                                                                )}

                                                                {hiddenColumns.includes('officeLocation') && (
                                                                    <td>{data?.location?.value}</td>
                                                                )}
                                                                {hiddenColumns.includes('homeLocation') && (
                                                                    <td>{data?.findAddress || "N/A"}</td>
                                                                )}
                                                                {hiddenColumns.includes('active') && (
                                                                    <td>
                                                                        <div className='d-flex gap-2 align-items-center'>
                                                                            {data?.active === true ? (
                                                                                <span className="d-inline-flex align-items-center gap-1">
                                                                                    {showPresence && (() => {
                                                                                        const presence = resolvePresence(data.userAccountID, data)
                                                                                        if (!presence.isOnline) return null
                                                                                        return (
                                                                                            <UserPresenceDot
                                                                                                isOnline
                                                                                                variant="inline"
                                                                                                tooltipHtml={buildPresenceTooltipHtmlForRow(data, presence)}
                                                                                            />
                                                                                        )
                                                                                    })()}
                                                                                    <span className="badge badge-light-success fs-7 fw-semibold">{labelKey.active}</span>
                                                                                </span>
                                                                            ) : (
                                                                                <span className="badge badge-light-danger fs-7 fw-semibold">{labelKey.inActive}</span>
                                                                            )}
                                                                            {excludeSupportUser !== true && (
                                                                                <div className="form-check form-switch">
                                                                                    <input
                                                                                        className="form-check-input cursor-pointer"
                                                                                        type="checkbox"
                                                                                        role="switch"
                                                                                        id={`flexSwitchCheckChecked-${data?.userAccountID}`}
                                                                                        checked={data?.active}
                                                                                        onChange={(e) => {
                                                                                            setIsChecked(e.target.checked);
                                                                                            setEditData(data);
                                                                                            expenseConfirmationModalHandler();
                                                                                        }}
                                                                                    />
                                                                                    <label className="form-check-label" htmlFor={`flexSwitchCheckChecked-${data?.userAccountID}`}></label>
                                                                                </div>
                                                                            )}

                                                                        </div>
                                                                    </td>
                                                                )}
                                                                {hiddenColumns.includes('createDT') && (
                                                                    <td><DateTimeComp formattedDate={data?.createDT} /></td>
                                                                )}
                                                                {hiddenColumns.includes('createdBy') && (
                                                                    <td>{data?.createdBy}</td>
                                                                )}
                                                                {hiddenColumns.includes('modifiedOn') && (
                                                                    <td><DateTimeComp formattedDate={data?.modifiedOn} /></td>
                                                                )}
                                                                {hiddenColumns.includes('modifiedBy') && (
                                                                    <td>{data?.modifiedBy}</td>
                                                                )}
                                                                {hiddenColumns.includes('lastLoginDt') && (
                                                                    <td>
                                                                        <LoginSourceWithDate sourceId={data?.lastLoginSourceId}>
                                                                            <DateTimeComp formattedDate={data?.lastLoginDt} />
                                                                        </LoginSourceWithDate>
                                                                    </td>
                                                                )}
                                                                {hiddenColumns.includes('lastLeaveDt') && (
                                                                    <td>
                                                                        {(() => {
                                                                            const presence = resolvePresence(data.userAccountID, data)
                                                                            const leaveUtc = presence.lastLeaveUtc || data?.lastLeaveDt
                                                                            return leaveUtc
                                                                                ? (
                                                                                    <LoginSourceWithDate sourceId={data?.lastLoginSourceId}>
                                                                                        <DateTimeComp formattedDate={leaveUtc} />
                                                                                    </LoginSourceWithDate>
                                                                                )
                                                                                : null
                                                                        })()}
                                                                    </td>
                                                                )}
                                                                {hiddenColumns.includes('actions') && excludeSupportUser !== true && (

                                                                    <td className='text-end sticky-table-actions' onClick={(e) => e.stopPropagation()}>
                                                                        <SsRowActions
                                                                            menuId={`user-row-actions-${data?.userAccountID}`}
                                                                            actions={[
                                                                                {
                                                                                    key: 'reset-password',
                                                                                    label: 'Reset Password',
                                                                                    hidden: !(roleID === constraintConfig.roleID.roleIDAdmin ||
                                                                                        roleID === constraintConfig.roleID.role1 ||
                                                                                        roleID === constraintConfig.roleID.role2),
                                                                                    tooltipId: 'Reset-Password',
                                                                                    icon: <i className='fa fa-key fa-fill' />,
                                                                                    onClick: () => {
                                                                                        setEditData(data)
                                                                                        passwordResetModalHandler()
                                                                                    },
                                                                                },
                                                                                {
                                                                                    key: 'impersonate',
                                                                                    label: labelKey.impersonateUser,
                                                                                    hidden: !((roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.roleExecutive) && data?.active === true),
                                                                                    tooltipId: 'impersonate',
                                                                                    icon: <KTSVG path='/media/icons/duotune/general/impersonate-user.svg' className='svg-icon-3' />,
                                                                                    onClick: () => {
                                                                                        setImpersonateData(data)
                                                                                        impersonateHandler()
                                                                                    },
                                                                                },
                                                                                {
                                                                                    key: 'update-user',
                                                                                    label: 'Update User',
                                                                                    tooltipId: 'update-user',
                                                                                    icon: <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />,
                                                                                    onClick: () => {
                                                                                        userDetailByIdTrigger({ id: data?.userAccountID })
                                                                                        setEditData(data)
                                                                                        editUserHandler()
                                                                                    },
                                                                                },
                                                                                {
                                                                                    key: 'update-email',
                                                                                    label: 'Update User Email',
                                                                                    hidden: !(roleID === constraintConfig.roleID.role2 || roleID === constraintConfig.roleID.role1),
                                                                                    tooltipId: 'update-user',
                                                                                    icon: <KTSVG path='/media/icons/duotune/communication/com011.svg' className='svg-icon-3' />,
                                                                                    onClick: () => {
                                                                                        setEditData(data)
                                                                                        updateUserEmailHandler()
                                                                                    },
                                                                                },
                                                                                {
                                                                                    key: 'user-detail',
                                                                                    label: labelKey.userDetails,
                                                                                    tooltipId: 'user-detail',
                                                                                    icon: <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-3' />,
                                                                                    onClick: () => {
                                                                                        setuserDetailData(data?.userAccountID)
                                                                                        userDetailHandler()
                                                                                    },
                                                                                },
                                                                            ]}
                                                                        />
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <tr>
                                                        <td colSpan={25}>
                                                            <NoRecordFound />
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        }

                                        {getAllUsersIsLoading && (
                                            <tr>
                                                <td colSpan={25} className="text-center">
                                                    <LoadingComponent />
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <Pagination
                                totalResult={getAllUsersData?.result?.totalRecords === 0 ? 0 : ((getAllUsersData?.result?.pageNumber - 1) * getAllUsersData?.result?.pageSize) + 1}
                                toResult={getAllUsersData?.result?.totalRecords === 0 ? 0 : Math.min(getAllUsersData?.result?.pageNumber * getAllUsersData?.result?.pageSize, getAllUsersData?.result?.totalRecords)}
                                ofResult={getAllUsersData?.result?.totalRecords}
                                onChange={handlePageSizeChange}
                                pageSize={pageSize}
                                onPageChange={handlePageClick}
                                pageCount={getAllUsersData?.result?.totalPages || 0}
                                currentPage={page}
                            />
                    </div>
                </div>
        </>
    )
}

export default UserPage